import DesignTokens from '@tableau/design-tokens';
import {
    CompactInlineAlertWidget,
    InlineAlertType,
    InlineAlertWidget,
    RadioButtonWithLabelGroupWidget,
    RadioButtonWithLabelOption,
    TabStyles,
    TextFieldWidget,
    TextLinkWidget} from '@tableau/widgets';
import React from 'react';
import { style } from 'typestyle';

import { Messages } from '~src/codegen/Localize';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { OAuthAccountDropdown } from '~src/connectivity/components/EditConnection/components/OAuthAccountDropdown';
import { CredentialOption } from '~src/connectivity/components/EditConnection/IEditConnectionDialog';
import { EditConnectionReactActionUtils, WIF_IMPERSONATION } from '~src/connectivity/components/EditConnection/utils/EditConnectionActionUtils';
import { IOAuthCredential } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import { WifAuthMode } from '~src/datasource/dataConnection/resources/DataConnections';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';
import { cssClass } from '~src/style';
import { applyColor } from '~src/style/ColorService';

const optionText: string = cssClass('edit-connection-auth-chooser', 'option-text', {
    marginBottom: TabStyles.Sizing.BaseUnit,
});

const optionSubText: string = cssClass('edit-connection-auth-chooser', 'option-subtext', {
    color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Typography.Color.Secondary)
});

const authenticationLabel: string = cssClass('edit-connection-auth-chooser', 'authentication-label', {
    marginTop: TabStyles.Sizing.BaseUnit * 3,
    marginBottom: TabStyles.Sizing.BaseUnit * 2,
    fontWeight: 'bold'
});

const rowClass = style({ width: '100%' });

// TextFieldWidget applies className to its root <div> (not the <input>); stretch that root to the
// full detail width so the service-account input matches the sibling fields and does not truncate
// long GCP service-account emails.
const serviceAccountFieldClass = style({ width: '100%' });

export type EditOAuthConnectionAuthChooserProps = Readonly<{
    // Single source of truth: the selected radio is derived from these, not stored separately.
    workgroupAuthMode: WifAuthMode | undefined;
    authFederationServiceAccount: string | undefined;
    credentialOption: CredentialOption;
    credentialId: string | undefined;
    credentials: IOAuthCredential[];
    credentialNoValueOptionText: string | undefined;
    // Applicability + capability, all static per row. wifModesAvailable holds the WIF modes the
    // connector offers (empty when none); the radio's presence, label, and service-account field are
    // driven off the offered mode, not off the derived radio which cannot tell the two WIF modes apart.
    isOAuthEmbeddedApplicable: boolean;
    wifModesAvailable: WifAuthMode[];
    isStoredWif: boolean;
    // A managed keychain provides the embedded credential but the user cannot pick a different account
    // for it; the warning explains why the dropdown is fixed, mirroring the stock OAuth dialog.
    usingManagedKeychain: boolean;
    addNewOAuthCredentialOptionValue: string | undefined;
    currentConnectionTypeDisplayName: string;
    // A single multi-field write so switching a radio never leaves a half-set row.
    onSelectRadio: (change: Partial<{
        workgroupAuthMode: WifAuthMode | undefined;
        authFederationServiceAccount: string | undefined;
        credentialOption: CredentialOption;
        credentialId: string | undefined;
    }>) => void;
    onServiceAccountChange: (serviceAccount: string | undefined) => void;
    handleCredentialIdChange: (newCredentialId: string) => void;
}>;

export function EditOAuthConnectionAuthChooser(props: EditOAuthConnectionAuthChooserProps) {
    const selectedRadio: EditConnectionReactActionUtils.EditConnectionAuthRadio = EditConnectionReactActionUtils.deriveAuthRadio({
        workgroupAuthMode: props.workgroupAuthMode,
        credentialOption: props.credentialOption
    });

    // The connector offers a single WIF mode; the radio's label and its service-account field depend on
    // WHICH mode, which deriveAuthRadio cannot answer (it collapses both WIF modes to one radio value).
    // Read the offered mode directly. A service account applies only to impersonation.
    const offersWif = props.wifModesAvailable.length > 0;
    const offeredWifIsImpersonation = props.wifModesAvailable[0] === WIF_IMPERSONATION;

    // Show the format error only for non-empty bad input; empty is "required" and is surfaced by the
    // disabled Save/Test buttons, not an inline message. Gated on the offered impersonation mode rather
    // than the derived radio so a standard-WIF row (no service account) is never treated as SA-bearing.
    const saInvalid = offeredWifIsImpersonation
        && !!props.authFederationServiceAccount
        && !EditConnectionReactActionUtils.isValidServiceAccount(props.authFederationServiceAccount);

    const learnMoreLink = (
        <TextLinkWidget
            className={learnMoreStyle}
            href={HelpService.getServerHelpUrlByCategory('managed-keychain')}
            target='_blank'
            testId={'learn-more'}
        >
            {Messages.learnMore()}
        </TextLinkWidget>
    );

    const oauthEmbeddedDisabled = EditConnectionReactActionUtils.oauthEmbeddedRadioDisabled({
        isStoredWif: !!props.isStoredWif,
        usingManagedKeychain: !!props.usingManagedKeychain,
        credentials: props.credentials,
        addNewOAuthCredentialOptionValue: props.addNewOAuthCredentialOptionValue,
    });
    const radioValues: EditConnectionReactActionUtils.EditConnectionAuthRadio[] = [];
    const buttonList: RadioButtonWithLabelOption<EditConnectionReactActionUtils.EditConnectionAuthRadio>[] = [];

    if (props.isOAuthEmbeddedApplicable) {
        radioValues.push('oauth-embedded');
        buttonList.push({
            label: <div className={optionText}>{Messages.editDataConnections_generic_embedCredential()}</div>,
            detail:
                <div>
                    <OAuthAccountDropdown
                        credentialOption={props.credentialOption}
                        credentials={props.credentials}
                        credentialId={props.credentialId}
                        credentialNoValueOptionText={props.credentialNoValueOptionText}
                        addNewOAuthCredentialOptionValue={props.addNewOAuthCredentialOptionValue}
                        currentConnectionTypeDisplayName={props.currentConnectionTypeDisplayName}
                        ariaLabel={Messages.editDataConnections_generic_embedCredential()}
                        handleCredentialIdChange={props.handleCredentialIdChange}
                    />
                    <label className={optionSubText}>{Messages.editDataConnections_generic_describeEmbedCredential()}</label>
                    {props.usingManagedKeychain ? (
                        <InlineAlertWidget
                            testId={'managed-keychain-alert'}
                            type={InlineAlertType.Warning}
                            children={<span>
                                {Messages.managedKeychainEditConnectionWarningMessage()}
                                {learnMoreLink}
                            </span>}
                        />
                    ) : null}
                </div>,
            testId: 'oauth-embedded',
            value: 'oauth-embedded',
            // Enable OAuth for stored WIF when a selectable credential exists and the row is not
            // managed keychain; still disable when there is no account and no Add New. W-23514330.
            disabled: oauthEmbeddedDisabled,
        });
    }

    if (offersWif) {
        radioValues.push('wif');
        buttonList.push({
            label: <div className={optionText}>{offeredWifIsImpersonation
                ? Messages.editConnection_authMode_wifImpersonation()
                : Messages.editConnection_authMode_wif()}</div>,
            // The service-account field is impersonation-only. Gate on the offered mode AND the radio
            // being selected: standard WIF (Snowflake) shows no field, impersonation shows it once chosen.
            detail: offeredWifIsImpersonation && selectedRadio === 'wif' ? (
                <div className={rowClass}>
                    <TextFieldWidget
                        className={serviceAccountFieldClass}
                        style={{ width: '100%' }}
                        onChange={(e) => props.onServiceAccountChange(e.target.value)}
                        testId={'wif-service-account-input'}
                        data-tb-test-id={'wif-service-account-input'}
                        value={props.authFederationServiceAccount}
                        // Matches the {1,256} upper bound of SERVICE_ACCOUNT_REGEX so the input does not
                        // truncate valid GCP service-account emails the validators accept.
                        maxLength={256}
                        label={Messages.editConnection_serviceAccount_label()}
                        valid={saInvalid ? false : undefined}
                    />
                    {saInvalid ? (
                        <div data-tb-test-id={'wif-service-account-error'} style={{ color: DesignTokens.color.feedback.default.error }}>
                            {Messages.editConnection_serviceAccount_invalidFormat()}
                        </div>
                    ) : null}
                </div>
            ) : null,
            testId: 'wif',
            value: 'wif',
        });
    }

    radioValues.push('no-auth');
    buttonList.push({
        label: <div className={optionText}>{Messages.editDataConnections_generic_noCredential()}</div>,
        detail: <div><label className={optionSubText}>{Messages.editDataConnections_generic_describeNoCredential()}</label></div>,
        testId: 'no-auth',
        value: 'no-auth',
        // A stored-WIF connection can switch to No Authentication (the server clears the federation mode
        // from all three stores so the connection prompts on reopen). OAuth-embedded is offered when a
        // selectable credential exists and the row is not managed keychain (W-23514330).
    });

    const checkedIndex = Math.max(0, radioValues.indexOf(selectedRadio));

    return (
        <div>
            <RadioButtonWithLabelGroupWidget<EditConnectionReactActionUtils.EditConnectionAuthRadio>
                className={authenticationLabel}
                label={<div className={authenticationLabel}>{Messages.authentication()}</div>}
                buttonList={buttonList}
                checkedIndex={checkedIndex}
                handleChange={(value) => props.onSelectRadio(EditConnectionReactActionUtils.authRadioStateChange(value, props.wifModesAvailable))}
            />
            {oauthEmbeddedDisabled && props.isStoredWif ? (
                <CompactInlineAlertWidget
                    children={Messages.editConnection_authMode_oauthEmbeddedDisabledHelp()}
                    type={InlineAlertType.Info}
                    testId={'edit-conn-revert-disabled-help'}
                />
            ) : null}
        </div>
    );
}
